<template #content>
      <HomeButton />
      <div class="setup-page">
        <!-- Заголовок -->
        <!-- <div class="setup-page__header">
          <h1 class="setup-page__title">Установка и настройка</h1>
        </div> -->

        <!-- Контент -->
        <div class="setup-page__content">
          <!-- Состояние загрузки -->
          <div v-if="connectStore.isLoading" class="setup-page__loading">
            <Loader />
          </div>

          <!-- Состояние ошибки -->
          <div v-else-if="connectStore.error" class="setup-page__error">
            <div class="error-message">
              <h3>{{ connectStore.error }}</h3>
              <BaseButton 
                text="Попробовать снова" 
                styles="primary-gradient"
                @on-submit="retryFetch"
              />
            </div>
          </div>

          <!-- Основной контент - отдельные страницы шагов -->
          <div v-else-if="connectStore.connectData && connectStore.currentApps.length > 0" class="setup-page__main">

            <!-- Общий прогресс-индикатор для всех шагов (не пересоздается) -->
            <div class="setup-page__progress-container">
              <ProgressCircle :progress="getStepProgress()">
                <IconStep1 v-if="currentStep === 0" />
                <IconStep2 v-else-if="currentStep === 1" />
                <IconStep3 v-else-if="currentStep === 2" />
                <IconStep4 v-else-if="currentStep === 3" />
              </ProgressCircle>
            </div>

            <!-- Шаг 1: Настройка на устройстве -->
            <div v-if="currentStep === 0" class="step-page">
              <div class="step-card">
                <div class="step-content">
                  <h3 class="step-title">Настройка на <span class="step-title--active">{{ currentOSName }}</span></h3>
                  <p class="step-description">Настройка VPN происходит в 3 шага и занимает пару минут</p>
                  <div class="step-actions">
                    <BaseButton 
                      text="Начать настройку на этом устройстве"
                      styles="secondary-100"
                      @on-submit="nextStep"
                    />
                    <!-- <BaseButton 
                      text="Установить на другом устройстве"
                      styles="secondary-100"
                      @on-submit="nextStep"
                    /> -->
                  </div>
                </div>
              </div>
            </div>

            <!-- Шаг 2: Установка приложения -->
            <div v-if="currentStep === 1" class="step-page">
              <div class="step-card">
                <div class="step-content">
                  <h3 class="step-title">Установка приложения</h3>
                  <p class="step-description">Установите приложение и вернитесь к этому экрану.</p>
                  
                  <div class="step-actions">
                      <BaseButton 
                        text="Установить"
                        styles="secondary-100"
                        @on-submit="openInstallModal"
                      />
                      <BaseButton 
                        text="Далее"
                        styles="secondary-200"
                        @on-submit="nextStep"
                      />
                      <BaseButton 
                        text="Назад"
                        styles="secondary-100"
                        @on-submit="prevStep"
                      />
                  </div>
                </div>
              </div>
            </div>

            <!-- Шаг 3: Подписка -->
            <div v-if="currentStep === 2" class="step-page">
              <div class="step-card">
                <div class="step-content">
                  <h3 class="step-title">Подписка</h3>
                  <p class="step-description">Добавьте подписку в приложение.</p>
                  
                  <div class="step-actions">
                    <BaseButton 
                      text="Добавить подписку"
                      styles="secondary-100"
                      @on-submit="addSubscription"
                    />
                    <BaseButton 
                      text="Далее"
                      styles="secondary-200"
                      @on-submit="nextStep"
                    />
                    <BaseButton 
                      text="Назад"
                      styles="secondary-100"
                      @on-submit="prevStep"
                    />
                  </div>
                </div>
              </div>
            </div>

            <!-- Шаг 4: Подписка добавлена -->
            <div v-if="currentStep === 3" class="step-page">
              <div class="step-card">
                <div class="step-content">
                  <h3 class="step-title step-title--sub-add">Подписка добавлена</h3>
                  <p class="step-description">Осталось только включить VPN в приложении, нажав на круглю кнопку, и готово! </p>
                  
                  <div class="step-actions">
                    <BaseButton 
                      text="Завершить настройку"
                      styles="secondary-100"
                      @on-submit="completeSetup"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
</template>
<script setup lang="ts">
import { onMounted, ref, computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useConnectStore, type OSType } from '@/entities/Subscription/model/connectStore'
import type { GetConnectItem } from '@/entities/Subscription/model/types'
import { useSubscriptionStore } from '@/entities/Subscription/model/subscriptionStore'
import { useModalStore } from '@/stores/useModal'
import { detectOS } from '@/shared/utils/system/detectOS'
import HomeButton from '@/ui/components/HomeButton.vue'
import Loader from '@/ui/components/Loader.vue'
import BaseButton from '@/ui/components/Base/BaseButton.vue'
import ProgressCircle from '@/ui/components/ProgressCircle.vue'
import IconStep1 from '@/assets/icons/instr-1.svg'
import IconStep2 from '@/assets/icons/instr-2.svg'
import IconStep3 from '@/assets/icons/instr-3.svg'
import IconStep4 from '@/assets/icons/instr-4.svg'
import AppInstallModal from '@/ui/components/Modal/AppInstallModal.vue'
import router from '@/router'
import { fireRealisticConfetti } from '@/shared/composables/fireRealisticConfetti'
import { SentryService } from '@/shared/lib/sentry/sentryService'

const route = useRoute()
const connectStore = useConnectStore()
const subscriptionStore = useSubscriptionStore()
const modalStore = useModalStore()

const currentStep = ref<number>(0)

// Состояние выбранного приложения
const selectedApp = ref<GetConnectItem | null>(null)

// Отслеживаем изменения currentStep
watch(currentStep, (newStep, oldStep) => {
  console.log('🔄 currentStep изменился:', oldStep, '->', newStep)
}, { immediate: true })

// Автоматическое определение ОС
const detectedOS = detectOS()

// Маппинг для приведения определенной ОС к типам API
const osMapping: Record<string, OSType> = {
  'iOS': 'ios',
  'Android': 'android',
  'macOS': 'macos',
  'Windows': 'windows',
  'Linux': 'linux'
}

// Получаем тип ОС для API
const currentOSType = computed<OSType>(() => {
  return osMapping[detectedOS] || 'android' // fallback на android
})

// Название ОС для отображения
const currentOSName = computed<string>(() => {
  return detectedOS
})

// Функция для расчета прогресса шага
// Функция для расчета прогресса текущего шага
function getStepProgress(): number {
  const progress = Math.min((currentStep.value + 1) * 25, 100) // 25%, 50%, 75%, 100%
  console.log(`🎯 CurrentStep=${currentStep.value}, progress=${progress}`)
  return progress
}

// Функция для открытия модалки установки приложения
function openInstallModal() {
  // Отправляем отчет в Sentry
  SentryService.trackButtonClick('Установить приложение', {
    step: currentStep.value,
    available_apps_count: connectStore.currentApps.length,
    os: currentOSName.value
  })
  
  if (connectStore.currentApps.length > 0) {
    modalStore.openModal({
      component: AppInstallModal,
      props: {
        availableApps: connectStore.currentApps,
        defaultAppIndex: 0,
        onAppSelected: handleAppSelected
      },
      showCloseButton: true,
      persistent: false
    })
  }
}

// Обработчик выбора приложения
function handleAppSelected(app: GetConnectItem) {
  selectedApp.value = app
  console.log('🎯 Выбрано приложение:', app.name)
}

// Функция для перехода к следующему шагу
function nextStep() {
  console.log('🟢 nextStep вызван. Текущий шаг:', currentStep.value)
  
  // Отправляем отчет в Sentry
  SentryService.trackUserAction('Переход к следующему шагу настройки', {
    from_step: currentStep.value,
    to_step: currentStep.value + 1,
    os: currentOSName.value
  })
  
  if (currentStep.value < 3) {
    currentStep.value++
    console.log('🟢 Шаг увеличен до:', currentStep.value)
  } else {
    console.log('🟢 Достигнут последний шаг')
  }

  if(currentStep.value === 3) {
    fireRealisticConfetti()
  }
}

// Функция для перехода к предыдущему шагу
function prevStep() {
  console.log('🔴 prevStep вызван. Текущий шаг:', currentStep.value)
  
  // Отправляем отчет в Sentry
  SentryService.trackUserAction('Переход к предыдущему шагу настройки', {
    from_step: currentStep.value,
    to_step: currentStep.value - 1,
    os: currentOSName.value
  })
  
  if (currentStep.value > 0) {
    currentStep.value--
    console.log('🔴 Шаг уменьшен до:', currentStep.value)
  } else {
    console.log('🔴 Достигнут первый шаг')
  }
}

// Функция для перехода к конкретному шагу
function goToStep(stepIndex: number) {
  if (stepIndex >= 0 && stepIndex <= 3) {
    currentStep.value = stepIndex
  }
}

// Функция для добавления подписки
function addSubscription() {
  // Используем выбранное приложение или первое из списка как fallback
  const appToUse = selectedApp.value || connectStore.currentApps[0]
  
  // Отправляем отчет в Sentry
  SentryService.trackButtonClick('Добавить подписку', {
    step: currentStep.value,
    app_name: appToUse?.name || 'unknown',
    os: currentOSName.value
  })
  
  if (appToUse) {
    console.log('🎯 Добавляем подписку через приложение:', appToUse.name)
    openLink(appToUse.subscription_add_link)
    nextStep()
  } else {
    console.error('❌ Нет доступных приложений для добавления подписки')
    SentryService.captureMessage('Попытка добавить подписку без доступных приложений', 'error')
  }
}

// Функция для завершения настройки
function completeSetup() {
  // Отправляем отчет в Sentry
  SentryService.trackUserAction('Завершение настройки VPN', {
    os: currentOSName.value,
    selected_app: selectedApp.value?.name || 'unknown',
    total_steps_completed: 4
  })
  
  sessionStorage.setItem('refreshDataOnHome', 'true')
  router.push('/')
  // Можно добавить дополнительную логику завершения
}

// Функция для открытия ссылок
function openLink(url: string) {
  window.open(url, '_blank')
}

// Функция повторной загрузки данных
async function retryFetch() {
  const subscriptionId = route.params.id as string || subscriptionStore.subscription?.id
  if (subscriptionId) {
    await connectStore.fetchConnectData(subscriptionId)
    // Устанавливаем автоматически определенную ОС
    connectStore.setSelectedOS(currentOSType.value)
  }
}

// Загрузка данных при монтировании компонента
onMounted(async () => {
  // Отправляем отчет о просмотре страницы настройки
  SentryService.trackPageView('Страница настройки VPN', {
    os: currentOSName.value,
    subscription_id: route.params.id as string || subscriptionStore.subscription?.id || null
  })
  
  // Получаем ID подписки из роута или из store
  const subscriptionId = route.params.id as string || subscriptionStore.subscription?.id
  
  if (!subscriptionId) {
    // Если нет ID подписки, сначала загружаем данные подписки
    if (!subscriptionStore.subscription) {
      await subscriptionStore.fetchSubscription()
    }
    
    // Повторно проверяем после загрузки
    const finalSubscriptionId = subscriptionStore.subscription?.id
    if (finalSubscriptionId) {
      await connectStore.fetchConnectData(finalSubscriptionId)
      // Устанавливаем автоматически определенную ОС
      connectStore.setSelectedOS(currentOSType.value)
      // Устанавливаем первое приложение как выбранное по умолчанию
      if (connectStore.currentApps.length > 0) {
        selectedApp.value = connectStore.currentApps[0]
      }
    } else {
      connectStore.error = 'Не удалось найти активную подписку'
    }
  } else {
    await connectStore.fetchConnectData(subscriptionId)
    // Устанавливаем автоматически определенную ОС
    connectStore.setSelectedOS(currentOSType.value)
    // Устанавливаем первое приложение как выбранное по умолчанию
    if (connectStore.currentApps.length > 0) {
      selectedApp.value = connectStore.currentApps[0]
    }
  }
})
</script>

<style lang="scss">
.setup-page {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
  overflow-x: hidden;
  overflow-y: hidden;

  &__header {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 32px;
  }
  
  &__title {
    font-size: 24px;
    font-weight: 600;
    color: $neutral-100;
    margin: 0;
  }
  
  &__content {
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    align-items: center;
    gap: 24px;
    height: 100%;
    width: 100%;
  }

  &__main {
    width: 100%;
  }

  &__progress-container {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: calc(var(--tg-viewport-height, var(--app-viewport-height, 100dvh)) - 200px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1;
    pointer-events: none;
    box-sizing: border-box;
  }
  
  &__loading,
  &__error,
  &__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 200px;
    text-align: center;
    color: $neutral-100;
  }
  
  &__error {
    .error-message {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 16px;
      
      h3 {
        color: $neutral-100;
        margin: 0;
      }
    }
  }
}

.step-page {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: var(--tg-viewport-height, var(--app-viewport-height, 100dvh));
  z-index: 1;
  box-sizing: border-box;
}

.setup-steps {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.step-card {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: var(--tg-viewport-height, var(--app-viewport-height, 100dvh));
  overflow: hidden;
  box-sizing: border-box;
  
  &.disabled {
    opacity: 0.5;
    pointer-events: none;
  }
  
  &.completed {
    background: rgba(255, 255, 255, 0.05);
  }
}

.step-icon {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
    height: calc(var(--tg-viewport-height, var(--app-viewport-height, 100dvh)) - 200px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
  box-sizing: border-box;
}

.step-circle {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  border: 3px solid rgba(255, 255, 255, 0.2);
  background: transparent;
  transition: all 0.3s ease;
  
  &.active {
    border-color: $primary-100;
    background: rgba($primary-100, 0.1);
    
    &::before {
      content: '';
      position: absolute;
      top: -6px;
      left: -6px;
      right: -6px;
      bottom: -6px;
      border-radius: 50%;
      border: 2px solid $primary-100;
      opacity: 0.3;
    }
  }
  
  &.completed {
    border-color: $primary-100;
    background: $primary-100;
    
    .step-icon-placeholder {
      display: none;
    }
    
    &::after {
      content: '✓';
      color: white;
      font-size: 32px;
      font-weight: bold;
    }
  }
}

.step-icon-placeholder {
  font-size: 32px;
  opacity: 0.7;
}

.step-content {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  justify-content: flex-end;
  width: 100%;
  z-index: 10;
  padding: 20px;
  background: linear-gradient(transparent, rgba(21, 21, 21, 0.8));
}

.step-title {
  font-size: 20px;
  font-weight: 600;
  color: $neutral-100;
  margin: 0;
  text-align: center;
    &--active {
      color: $primary-100;
    }

    &--sub-add {
      color: $primary-100;
    }
}

.step-description {
  font-size: 14px;
  color: $neutral-40;
  line-height: 1.5;
  margin: 0;
  text-align: center;
}

.step-actions {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 28px;

  & > .button-base-block {
    & > .button-base {
      padding: 16px 20px;
      justify-content: center;
    }
  }
}

.step-completed {
  display: flex;
  align-items: center;
  gap: 8px;
  color: $primary-100;
  font-weight: 500;
  margin-top: 8px;
  
  span {
    font-size: 16px;
  }
}
</style>